// Client keepalive. The server pings every 30s (apiv3 WS_LOG_INTERVAL), but only
// for connections it has already registered — i.e. after a successful auth
// handshake. This keeps traffic flowing from our side too, so a socket that is
// open but not yet authenticated, or one whose server ping loop has stalled,
// still resets the idle timers on the ALB (60s) and CloudFront.
const HEARTBEAT_MS = 25_000;

export let wsConn;

// The socket outlives any single component instance, so callbacks are held in
// one mutable slot rather than baked into the socket at construction: a
// remounted host rebinds by calling WS() again, and the live socket always
// drives the currently mounted handler instead of a stale closure.
let handlers = { msgCb: null, openCb: null, closeCb: null };
let socketRef = null;

// A queued send polls for the socket to open. Bounded so a payload can never
// outlive the socket it was queued against.
const SEND_WAIT_INTERVAL = 500;
const SEND_WAIT_MAX_ATTEMPTS = 20;

export function WS(url, msgCb, openCb, closeCb) {
    handlers = { msgCb, openCb, closeCb };

    // A closed socket cannot be revived — only reuse the connection while it
    // is still connecting/open, otherwise build a fresh one (reconnect path).
    if (wsConn && wsConn.readyState() <= WebSocket.OPEN) {
        return wsConn;
    }
    const socket = new WebSocket(url);
    socketRef = socket;

    socket.onopen = (event) => {
        handlers.openCb?.(event)
    };

    socket.onclose = (event) => {
        handlers.closeCb?.(event)
    };

    socket.onmessage = (event) => {
        if (event.data === 'ping') {
            return
        }
        const msg = JSON.parse(event.data)
        handlers.msgCb?.(msg.topic, msg.event, msg.data)
    };

    socket.onerror = (error) => {
        // Error handled silently
    };

    wsConn = {
        readyState() {
            return socket.readyState;
        },
        _send(msg) {
            this.waitForConnection(() => {
                socket.send(msg);
            });
        },
        waitForConnection(callback, attempt = 0) {
            if (socket.readyState === WebSocket.OPEN) {
                callback();
                return;
            }
            // CLOSING/CLOSED can never become OPEN again, so drop the payload
            // rather than poll a dead socket forever. The reconnect's onOpen
            // re-issues the auth and every subscription anyway.
            if (socket.readyState >= WebSocket.CLOSING || attempt >= SEND_WAIT_MAX_ATTEMPTS) {
                return;
            }
            setTimeout(() => {
                this.waitForConnection(callback, attempt + 1);
            }, SEND_WAIT_INTERVAL);
        },
        auth(token) {
            // Returns whether the handshake was actually sent. Without a token
            // the socket would sit open and unauthenticated forever — the server
            // waits for an auth frame and sends nothing — so report it and let
            // the caller close and re-auth rather than failing silently.
            if (!token) return false;

            this._send(JSON.stringify({
                type: 'auth',
                token,
            }));
            return true;
        },
        close(code) {
            socket.close(code);
        },
        subscribe(topic) {
            if (!(topic && (typeof topic === 'string') ) || !topic.length > 0) return;

            this._send(JSON.stringify({
                type: 'action',
                action: 'sub',
                topic,
            }));
        },
        unsubscribe(topic) {
            if (!(topic && (typeof topic === 'string') ) || !topic.length > 0) return;

            this._send(JSON.stringify({
                type: 'action',
                action: 'unsub',
                topic,
            }));
        },
        publish(topic, event, data) {
            if (!(topic && (typeof topic === 'string') ) || !topic.length > 0) return;
            if (!(event && (typeof event === 'string') ) || !event.length > 0) return;
            if (!(data && (typeof data === 'object'))) return;

            this._send(JSON.stringify({
                type: 'action',
                action: 'pub',
                topic,
                event,
                data
            }));
        },
        rpc(name, args) {
            this._send(JSON.stringify({
                'type': 'action',
                'action': 'rpc',
                'name': name,
                'args': args
            }));
        }
    }
    return wsConn;
}

// Tear the connection down for good. Detaching onclose first stops the host's
// reconnect chain from firing on a deliberate close — otherwise logging out
// leaves an authenticated socket reconnecting with a stale token forever.
export function closeWS() {
    handlers = { msgCb: null, openCb: null, closeCb: null };
    if (socketRef) {
        socketRef.onopen = null;
        socketRef.onclose = null;
        socketRef.onmessage = null;
        socketRef.onerror = null;
        socketRef.close();
        socketRef = null;
    }
    wsConn = undefined;
}
